import { memo, useState } from 'react'

import classNames from 'classnames'
import { Button, Container, Typography } from '@cleartrip/ct-design-components'

import { ChevronRight } from '@/assets/icons'
import EliteUnlockSoonModal from '@/components/EliteAfterBooking/EliteUnlockSoonModal'
import {
    ELITE_MEMBERSHIP_ASSETS,
    ELITE_MEMBERSHIP_CHANGE_DATE_FEE,
    ELITE_MEMBERSHIP_STRIP_ELITE,
    ELITE_MEMBERSHIP_STRIP_GUEST,
    ELITE_MEMBERSHIP_STRIP_PROGRESS,
    VIEW_ALL_BENEFITS_BUTTON_TEXT
} from '@/constants/components/eliteMembership'
import { ELITE_BANNER_HEIGHT_PX } from '@/constants/unifiedWidgetStack.constants'

import styles from './UnlockEliteBanner.module.scss'

export type UnlockEliteBannerUiVariant = 'guest' | 'member_progress' | 'elite'

type Props = {
    variant?: UnlockEliteBannerUiVariant
    stripLabel?: string
    showLogin?: boolean
    changeDateFee?: number | string
    onLoginClick: () => void
    onArrowClick?: () => void
    headlineText?: string
    stripText?: string
    enableUnlockSoonModal?: boolean
    onBenefitsPopupLoad?: () => void
    onViewBenefitsClick?: () => void
    onBenefitsPopupDismiss?: () => void
}

function stripCopy(variant: UnlockEliteBannerUiVariant): string {
    switch (variant) {
        case 'guest':
            return ELITE_MEMBERSHIP_STRIP_GUEST
        case 'member_progress':
            return ELITE_MEMBERSHIP_STRIP_PROGRESS
        case 'elite':
            return ELITE_MEMBERSHIP_STRIP_ELITE
        default:
            return ELITE_MEMBERSHIP_STRIP_GUEST
    }
}

function UnlockEliteBanner(props: Props) {
    const {
        variant = 'guest',
        stripLabel: stripLabelProp,
        showLogin: showLoginProp,
        changeDateFee = ELITE_MEMBERSHIP_CHANGE_DATE_FEE,
        onLoginClick,
        onArrowClick,
        headlineText,
        stripText: stripFromApi,
        enableUnlockSoonModal = false,
        onBenefitsPopupLoad,
        onViewBenefitsClick,
        onBenefitsPopupDismiss
    } = props

    const [showUnlockSoonModal, setShowUnlockSoonModal] = useState(false)

    const resolvedHeadline = headlineText?.trim() ? headlineText.trim() : `Change dates at just ₹${changeDateFee}`
    const stripLabel = stripFromApi?.trim()
        ? stripFromApi.trim()
        : stripLabelProp?.trim()
          ? stripLabelProp.trim()
          : stripCopy(variant)
    const showLogin = showLoginProp ?? variant === 'guest'

    const handleArrowClick = () => {
        onArrowClick?.()
        if (enableUnlockSoonModal) {
            setShowUnlockSoonModal(true)
        }
    }

    return (
        <Container
            className={classNames(styles.root, 'p-relative', 'w-100p', 'br-12')}
            css={{ height: ELITE_BANNER_HEIGHT_PX }}
        >
            <Container className={classNames(styles.curveClip, 'p-absolute', 'z-0', 'o-hidden', 'br-12')} aria-hidden>
                <img
                    className={classNames(styles.softBgSvg, 'p-absolute', 't-0', 'r-0', 'd-block')}
                    src={ELITE_MEMBERSHIP_ASSETS.bannerBg}
                    alt=""
                />
            </Container>

            <Container className={classNames(styles.calendarCluster, 'p-absolute', 'r-0')} aria-hidden>
                <Container className={classNames(styles.calendarInner, 'p-absolute', 'flex', 'flex-middle', 'flex-center')}>
                    <img className={classNames(styles.calendarMain, 'd-block')} src={ELITE_MEMBERSHIP_ASSETS.calendar} alt="" />
                </Container>
                <img className={classNames(styles.starFlank, styles.starFlankLeft2, 'p-absolute')} src={ELITE_MEMBERSHIP_ASSETS.star} alt="" />
                <img className={classNames(styles.starFlank, styles.starFlankLeft, 'p-absolute')} src={ELITE_MEMBERSHIP_ASSETS.star} alt="" />
                <img className={classNames(styles.starFlank, styles.starFlankRight, 'p-absolute')} src={ELITE_MEMBERSHIP_ASSETS.star} alt="" />
            </Container>

            <Container className={classNames(styles.inner, 'p-relative', 'flex', 'flex-column', 'p-4')}>
                <Container className={classNames(styles.headerRow, 'flex', 'flex-middle')}>
                    <Container className={classNames(styles.elitePill, 'flex-inline', 'flex-middle', 'flex-center', 'h-5')}>
                        <img className={classNames(styles.eliteMark, 'd-block')} src={ELITE_MEMBERSHIP_ASSETS.elite} alt="Elite" />
                    </Container>
                    <Container className={classNames('flex-inline', 'flex-middle', 'flex-no-shrink', 'h-5')}>
                        <img className={classNames(styles.membershipWordmark, 'd-block')} src={ELITE_MEMBERSHIP_ASSETS.membership} alt="Membership" />
                    </Container>
                </Container>

                <Container className={classNames('flex', 'flex-nowrap', 'flex-middle', 'w-100p', 'flex-gap-4')}>
                    <Container className={styles.headlineCol}>
                        <Typography
                            variant="HM2"
                            componentNode="span"
                            colorCode="var(--Neutral-White, #FFFFFF)"
                            className={classNames(styles.headline, 'd-block', 'w-100p')}
                        >
                            {resolvedHeadline}
                        </Typography>
                    </Container>

                    <Container
                        className={classNames(
                            styles.whiteStrip,
                            'p-relative',
                            'flex',
                            'py-2',
                            'px-3',
                            'br-12',
                            showLogin ? 'flex-middle flex-nowrap flex-between flex-gap-3' : 'flex-column'
                        )}
                    >
                        <Typography
                            variant="B2"
                            componentNode="span"
                            colorCode="var(--Neutral-Primary, #1A1A1A)"
                            className={classNames(styles.stripText, 'flex-1')}
                        >
                            {stripLabel}
                        </Typography>
                        {showLogin ? (
                            <Button variant="contained" size="small" minWidth="52px" onClick={onLoginClick}>
                                Login
                            </Button>
                        ) : (
                            <Typography color='link' isClickable={true} variant='L3' onClick={handleArrowClick}>
                                {VIEW_ALL_BENEFITS_BUTTON_TEXT}
                            </Typography>
                        )}
                    </Container>
                </Container>
            </Container>

            {enableUnlockSoonModal && (
                <EliteUnlockSoonModal
                    open={showUnlockSoonModal}
                    onClose={() => setShowUnlockSoonModal(false)}
                    onLoad={onBenefitsPopupLoad}
                    onViewAllBenefitsClick={onViewBenefitsClick}
                    onDismiss={onBenefitsPopupDismiss}
                />
            )}
        </Container>
    )
}

export default memo(UnlockEliteBanner)
